import React from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import {
  BodyText,
  Button,
  HeaderText,
  Stack,
} from '../../../ui';
import { sendClick } from '../utils';
import useProvider from '../../../../hooks/useProvider';
import { PrivilegedProvider } from '../../../../api/Providers';

export type WrappedECPProps = {
  providerId: number | string;
  onEdit: () => void;
};

export type EditableCancellationPolicyProps = {
  provider: Pick<PrivilegedProvider,
  'name'
  | 'cancellation_policy_text'
  | 'late_cancellation_fee_percent'
  | 'no_show_fee_percent'
  | 'can_process_payments'
  >;
  onEdit: () => void;
};

export const EditableCancellationPolicyBase: React.FCWithChildren<EditableCancellationPolicyProps> = React.memo(
  ({
    onEdit,
    provider,
  }) => {
    if (!provider?.can_process_payments) return null;

    const onClickEdit = sendClick('Update Cancellation Policy Clicked')(onEdit);

    return provider?.cancellation_policy_text ? (
      <Stack
        borderWidth={1}
        borderStyle="solid"
        borderColor={BrandColor.LightGrey}
        padding={20}
        alignItems="center"
        space={20}
      >
        <HeaderText level={3}>
          Late Cancellation Policy
        </HeaderText>
        <Inline>
          <Stack paddingX={20} alignItems="center">
            <BodyText
              color={BrandColor.FloraDark}
              appearance="larger"
              weight="bold"
              testID="lateFeePercent"
            >
              {provider?.late_cancellation_fee_percent}%
            </BodyText>
            <BodyText
              color={BrandColor.Metal}
            >
              Late cancel fee
            </BodyText>
          </Stack>
          <Box color={BrandColor.LightGrey} flexBasis={1} flexGrow={0} />
          <Stack paddingX={20} alignItems="center">
            <BodyText
              color={BrandColor.FloraDark}
              appearance="larger"
              weight="bold"
              testID="noShowFeePercent"
            >
              {provider?.no_show_fee_percent}%
            </BodyText>
            <BodyText
              color={BrandColor.Metal}
            >
              No-show fee
            </BodyText>
          </Stack>
        </Inline>
        <Button
          appearance="link-lowercase"
          size="medium"
          onClick={onClickEdit}
          testID="updateCancellationPolicyLink"
        >
          Update cancellation policy
        </Button>
      </Stack>
    ) : (
      <Stack
        borderWidth={1}
        borderStyle="solid"
        borderColor={BrandColor.LightGrey}
        padding={20}
        alignItems="center"
      >
        <Button appearance="link-lowercase" size="medium" onClick={onClickEdit}>
          Set up your cancellation policy
        </Button>
      </Stack>
    );
  },
);

/**
 * This wrapper is responsible for fetching the provider information for the editable state
 * TODO: this logic should be moved up to the consumer once the profile is refactored more
 */
const EditableCancellationPolicy: React.FCWithChildren<WrappedECPProps> = React.memo(
  ({ onEdit, providerId }) => {
    const { provider } = useProvider(providerId);

    return (
      <EditableCancellationPolicyBase
        provider={provider}
        onEdit={onEdit}
      />
    );
  },
);

export default EditableCancellationPolicy;
